import * as React from 'react';

import { Check } from '@freelancelabs/icons';
import { clsx } from '@freelancelabs/utils';

import { generateUID } from '../../../helpers';
import { useInstance } from '../../../hooks';
import { Badge } from '../badge';

export type RadioSize = 'small' | 'medium';

interface RadioGroupOptionsProps<T> {
    value: T;
    label: string;
    /**
     * Second line of a `category="card"` option: what the choice *is*, under the
     * sentence saying what it does. Ignored by the other categories, whose row has
     * no room for it.
     */
    description?: string;
    /**
     * Qualificatif court posé à côté du titre d'une carte, la pastille « Recommandé »
     * de la maquette du back-office. C'est une **chaîne** et non un nœud pour la même
     * raison que `label` : une carte porte un choix, pas une mise en page.
     *
     * Attention, il annote et ne contraint pas. Un choix qu'on ne peut PAS faire se
     * dit avec `disabled`, dont la raison va dans `description` : recommander est
     * l'inverse, les deux cartes restent ouvertes.
     */
    badge?: string;
    disabled?: boolean;
}

export interface RadioGroupProps<T> extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
    options: RadioGroupOptionsProps<T>[];
    onValue?: (arg0: T) => void;
    children?: React.ReactNode;
    /**
     * `card` lays the options out as bordered tiles instead of a column of dots:
     * the V2 mockup's `.carte-type`, for a choice that carries a screen rather than
     * qualifying a field. Each tile shows `label` and `description`.
     */
    category?: 'default' | 'check' | 'card';
    size?: RadioSize;
}

const RadioBase = <T extends React.InputHTMLAttributes<T>['value']>({
    id,
    options,
    value,
    onValue,
    children,
    disabled,
    className,
    onChange,
    size = 'medium',
    category = 'default',
    ...rest
}: RadioGroupProps<T>) => {
    const prefixId = useInstance(() => generateUID('radio'));
    /**
     * Attention, A card changes the **box around** the choice, not the control inside it, so
     * its dot keeps the `default` classes: every hover, focus and checked rule is
     * written as a sibling of those. Re-declaring them under a `radio-card-*` name
     * would be the second copy, i.e. the one that stops matching the first the day
     * either is fixed.
     */
    const dotCategory = category === 'card' ? 'default' : category;
    return (
        <div className={clsx('radio-group', `radio-group--${category}`, className)}>
            {options.map((option) => {
                const id = `${prefixId}-${option.value?.toString()}`;
                const checked = value === option.value;
                return (
                    <label
                        key={id}
                        htmlFor={id}
                        className={clsx(
                            'radio-label',
                            `radio-${category}-label`,
                            option.disabled && 'is-disabled',
                            checked && 'is-checked',
                            `radio-${size}`
                        )}
                    >
                        <input
                            id={id}
                            value={option.value}
                            onChange={(e) => {
                                onChange?.(e);
                                onValue?.(e.target.value as T);
                            }}
                            checked={checked}
                            type="radio"
                            className={clsx('radio', `radio-${dotCategory}`, checked && 'is-checked')}
                            disabled={option.disabled}
                            {...rest}
                        />
                        {dotCategory === 'default' && (
                            // `no-children` only spaces the dot when nothing else does;
                            // a card gets its spacing from the tile's own `gap`.
                            <span
                                className={clsx(
                                    'radio-default-fakeradio',
                                    children || category === 'card' ? '' : 'no-children'
                                )}
                            />
                        )}
                        {category === 'card' ? (
                            // `children` is deliberately not honoured here: it is a single
                            // node rendered inside *every* option, which cannot carry a
                            // per-option title and subtitle.
                            <span className="radio-card-content">
                                <span className="radio-card-title">
                                    <b>{option.label}</b>
                                    {option.badge && (
                                        <Badge category="brand" size="small">
                                            {option.badge}
                                        </Badge>
                                    )}
                                </span>
                                {option.description && (
                                    <small className="radio-card-description">{option.description}</small>
                                )}
                            </span>
                        ) : (
                            children || option.label
                        )}
                        {category === 'check' && (
                            <>
                                <div className="radio-check-space" />
                                <div className="radio-check-fakecheck">
                                    <Check className={clsx('radio-check-fakecheck-icon', checked && 'is-checked')} />
                                </div>
                            </>
                        )}
                    </label>
                );
            })}
        </div>
    );
};

export default RadioBase;
